/**
 * 데모 모드 샘플 데이터
 *
 * 여기 있는 값은 전부 가짜다. 실제 고객사 데이터를 넣지 말 것.
 *
 * 날짜는 "오늘"을 기준으로 계산한다. 고정 날짜를 박아 두면 몇 달 뒤 데모를 켰을 때
 * 상품 목록 기본 필터(행사 있음)에 아무것도 안 걸려서 빈 화면이 되기 때문이다.
 */

import { DEMO_COMPANY_NO, DEMO_USER_NO, DEMO_USER_EMAIL } from './demoMode';

const pad = (n) => String(n).padStart(2, '0');

/** 오늘로부터 n일 뒤의 'YYYY-MM-DD' */
export function dayFromNow(n) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 오늘로부터 n일 뒤의 'YYYY-MM-DD HH:mm' */
export function dateTimeFromNow(n, hh = 9, mm = 30) {
  return `${dayFromNow(n)} ${pad(hh)}:${pad(mm)}`;
}

// ────────────────────────────────────────────────────────────────
// 회사 / 사용자
// ────────────────────────────────────────────────────────────────

export const DEMO_COMPANY = {
  업체번호: DEMO_COMPANY_NO,
  업체명: '트립박스 데모 여행사',
  유저번호: DEMO_USER_NO,
  유저명: '김데모',
  회사로고: '',
};

export const DEMO_STAFF = {
  업체번호: DEMO_COMPANY_NO,
  유저번호: DEMO_USER_NO,
  한글이름: '김데모',
  유저명: '김데모',
  아이디: DEMO_USER_EMAIL,
  이메일: DEMO_USER_EMAIL,
  대표이메일: DEMO_USER_EMAIL,
  휴대폰: '010-0000-0000',
  대표전화: '02-6080-8800',
  생년월일: '1990-01-01',
  프로필: '',
  권한: '관리자',
};

// ────────────────────────────────────────────────────────────────
// 수동코드 (도시 / 담당자 / 상품타입 등 드롭다운 재료)
//   응답 형태: { 코드, 코드명 }
// ────────────────────────────────────────────────────────────────

export const DEMO_CODES = {
  도시: [
    { 코드: '9101', 코드명: '다낭' },
    { 코드: '9102', 코드명: '방콕' },
    { 코드: '9103', 코드명: '오사카' },
    { 코드: '9104', 코드명: '파리' },
    { 코드: '9105', 코드명: '제주' },
  ],
  담당자: [
    { 코드: '990001', 코드명: '김데모' },
    { 코드: '990002', 코드명: '이샘플' },
    { 코드: '990003', 코드명: '박체험' },
  ],
  투어상품타입: [
    { 코드: '2001', 코드명: '패키지' },
    { 코드: '2002', 코드명: '자유여행' },
    { 코드: '2003', 코드명: '현지투어' },
    { 코드: '2004', 코드명: '기타' },
    // 일반상품(단품) 화면이 이 타입으로 목록을 거르고 등록도 이로 고정한다.
    { 코드: '2005', 코드명: '단품' },
  ],
  호텔상품타입: [
    { 코드: '2101', 코드명: '호텔' },
    { 코드: '2102', 코드명: '리조트' },
  ],
  국가: [
    { 코드: 'VN', 코드명: '베트남' },
    { 코드: 'TH', 코드명: '태국' },
    { 코드: 'JP', 코드명: '일본' },
    { 코드: 'FR', 코드명: '프랑스' },
    { 코드: 'KR', 코드명: '대한민국' },
  ],
  통화: [
    { 코드: 'KRW', 코드명: '원화' },
    { 코드: 'USD', 코드명: '미국 달러' },
  ],
  // 스마트 상품 등록 1단계의 [통화] 드롭다운이 쓰는 종류
  통화코드: [
    { 코드: 'KRW', 코드명: 'KRW (원)' },
    { 코드: 'USD', 코드명: 'USD (달러)' },
    { 코드: 'JPY', 코드명: 'JPY (엔)' },
  ],
};

/**
 * 상세페이지의 "구역" 목록.
 * 구역은 글을 상세페이지의 어느 자리에 보여줄지 정하는 칸막이다. 서랍이라고 보면 된다.
 * 스마트 등록 2단계 검토 탭에서도 각 항목을 어느 구역에 넣을지 고를 때 쓰인다.
 */
export const DEMO_CONTENT_TYPES = [
  { 코드: '5001', 코드명: '일정소개', 컨텐츠종류코드: '5001', 컨텐츠명: '일정소개' },
  { 코드: '5002', 코드명: '포함사항', 컨텐츠종류코드: '5002', 컨텐츠명: '포함사항' },
  { 코드: '5003', 코드명: '불포함사항', 컨텐츠종류코드: '5003', 컨텐츠명: '불포함사항' },
  { 코드: '5004', 코드명: '유의사항', 컨텐츠종류코드: '5004', 컨텐츠명: '유의사항' },
  { 코드: '5005', 코드명: '가격/재고', 컨텐츠종류코드: '5005', 컨텐츠명: '가격/재고' },
];

// ────────────────────────────────────────────────────────────────
// 상세페이지 설정 화면
//   손님이 실제로 보는 상품 소개 페이지를 만드는 곳.
//   대표이미지 · 미팅장소 · 상품 상세 정보 · 일정표 네 군데를 채운다.
// ────────────────────────────────────────────────────────────────

/**
 * 데모용 사진.
 * 외부 이미지 주소를 쓰면 네트워크 상태에 따라 깨진 사진이 나온다.
 * 데모는 어디서 열어도 같은 화면이 나와야 하므로 SVG 를 그 자리에서 그려 쓴다.
 */
export function demoPhoto(label, from, to) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="960" height="540" viewBox="0 0 960 540">
<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
<stop offset="0%" stop-color="${from}"/><stop offset="100%" stop-color="${to}"/>
</linearGradient></defs>
<rect width="960" height="540" fill="url(#g)"/>
<circle cx="770" cy="120" r="58" fill="#ffffff" opacity="0.22"/>
<path d="M0 400 L240 270 L430 400 L620 250 L960 430 L960 540 L0 540 Z" fill="#ffffff" opacity="0.18"/>
<text x="48" y="486" font-family="'Malgun Gothic','Apple SD Gothic Neo',sans-serif" font-size="40" font-weight="700" fill="#ffffff">${label}</text>
<text x="48" y="524" font-family="'Malgun Gothic','Apple SD Gothic Neo',sans-serif" font-size="20" fill="#ffffff" opacity="0.85">데모 샘플 이미지</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** 대표이미지 (DBO.RCT_S_상품_대표이미지). 맨 앞 사진이 목록과 상세페이지 맨 위에 크게 나온다. */
export const DEMO_REP_IMAGES = [
  { 이미지번호: 3101, 이미지명: '다낭 미케 비치', 이미지경로: demoPhoto('다낭 미케 비치', '#0284c7', '#22d3ee'), 노출순서: 1 },
  { 이미지번호: 3102, 이미지명: '바나힐 골든브릿지', 이미지경로: demoPhoto('바나힐 골든브릿지', '#7c3aed', '#c084fc'), 노출순서: 2 },
  { 이미지번호: 3103, 이미지명: '호이안 올드타운 야경', 이미지경로: demoPhoto('호이안 올드타운', '#b45309', '#fbbf24'), 노출순서: 3 },
  { 이미지번호: 3104, 이미지명: '리조트 수영장', 이미지경로: demoPhoto('리조트 수영장', '#047857', '#34d399'), 노출순서: 4 },
  { 이미지번호: 3105, 이미지명: '다낭 대성당', 이미지경로: demoPhoto('다낭 대성당', '#be123c', '#fb7185'), 노출순서: 5 },
];

/** 미팅장소 (DBO.TRP_S_투어미팅장소설정값 → 투어미팅비고) */
export const DEMO_MEETING_PLACE =
  '인천공항 제1여객터미널 3층 M 카운터 앞\n트립박스 데모 팻말을 들고 있습니다. 출발 3시간 전까지 도착해 주세요.\n현지 도착 후에는 다낭공항 도착층 3번 게이트 앞에서 가이드가 기다립니다.';

/** 선택옵션 (DBO.TRP_S_선택옵션). 추가 요금을 받는 옵션 상품. 이용요금은 성인/소아/유아 */
export const DEMO_SELECT_OPTIONS = [
  { 서비스번호: 5201, 한글명: '전신 마사지 90분', 설명: '3일차 저녁 현지 스파 이용', 이용요금: '60000/40000/0', 요금부과방식: '1인당' },
  { 서비스번호: 5202, 한글명: '호이안 소원배 체험', 설명: '투본강 야간 등불 배 (2인 1척)', 이용요금: '25000/25000/0', 요금부과방식: '1인당' },
  { 서비스번호: 5203, 한글명: '공항 픽업 차량 단독 이용', 설명: '왕복 기준, 4인승', 이용요금: '120000/0/0', 요금부과방식: '1건당' },
];

/**
 * 상품 상세 정보 (구역별 글).
 * 각 구역의 컨텐츠는 JSON 문자열 배열로 내려온다. 화면이 그대로 파싱해서 카드로 그린다.
 *
 * '일정소개' 구역은 일부러 비워 둔다. 여기에 내용이 들어 있으면 화면이
 * "일정표 편집" 섹션을 감추기 때문이다. 데모에서는 일정표 편집을 보여줘야 한다.
 */
export const DEMO_DETAIL_CONTENT_ITEMS = {
  포함사항: [
    {
      컨텐츠번호: 6101,
      컨텐츠명: '포함 내역',
      테그: '<ul><li>왕복 항공료 및 유류할증료</li><li>4성급 리조트 4박 (2인 1실 기준)</li><li>일정표상 조식 4회 · 중식 3회 · 석식 2회</li><li>전 일정 한국어 가이드 동행</li><li>전용 차량 및 입장료</li><li>해외여행자보험 (최대 1억원)</li></ul>',
    },
  ],
  불포함사항: [
    {
      컨텐츠번호: 6102,
      컨텐츠명: '불포함 내역',
      테그: '<ul><li>개인 경비 및 매너팁 (1인 1일 USD 5 권장)</li><li>선택 관광 비용</li><li>싱글룸 사용료 (1박 40,000원)</li><li>베트남 입국 시 개인 물품 통관 비용</li></ul>',
    },
  ],
  유의사항: [
    {
      컨텐츠번호: 6103,
      컨텐츠명: '예약 전 꼭 확인',
      테그: '<p><strong>여권 유효기간이 출발일 기준 6개월 이상</strong> 남아 있어야 합니다.</p><ul><li>항공 스케줄은 현지 사정에 따라 변경될 수 있습니다.</li><li>일정 순서는 날씨·교통 상황에 따라 바뀔 수 있습니다.</li><li>출발 15일 전부터 취소 수수료가 발생합니다.</li></ul>',
    },
    {
      컨텐츠번호: 6104,
      컨텐츠명: '아동 요금 기준',
      테그: '<p>소아는 만 2세 이상 12세 미만, 유아는 만 2세 미만입니다. 유아는 좌석과 침대가 제공되지 않습니다.</p>',
    },
  ],
};

/** 일정타입 (DBO.RCT_S_상품일정타입). 같은 상품에 일정이 여러 벌 필요할 때 늘린다. */
export const DEMO_ITINERARY_TYPES = [
  { 일정타입번호: 6001, 일정타입명: '기본일정', 일정타입박수: 4, 일정타입일수: 5, 상품일정타입: 'Multi Day Tour', 사용여부: true },
];

/**
 * 일정표 (DBO.RCT_S_상품일정_02).
 *   table1 = 일차별 일정 카드, table3 = 일차별 식사, table5 = 일차별 호텔
 */
export const DEMO_ITINERARY_DETAIL = {
  table1: [
    { 일차: 1, 상품일정번호: 7101, 일정명: '인천국제공항 출발', 일정내용: '미팅 후 출국 수속을 진행합니다.', 시작시간: '07:30', 종료시간: '10:30' },
    { 일차: 1, 상품일정번호: 7102, 일정명: '다낭국제공항 도착', 일정내용: '가이드 미팅 후 전용 차량으로 이동합니다.', 시작시간: '13:20', 종료시간: '14:30' },
    { 일차: 1, 상품일정번호: 7103, 일정명: '리조트 체크인 · 자유시간', 일정내용: '수영장과 프라이빗 비치를 이용하실 수 있습니다.', 시작시간: '15:00', 종료시간: '' },
    { 일차: 2, 상품일정번호: 7104, 일정명: '바나힐 종일 관광', 일정내용: '케이블카로 산 정상까지 오릅니다. 프랑스 마을과 정원을 둘러봅니다.', 시작시간: '08:30', 종료시간: '16:00' },
    { 일차: 2, 상품일정번호: 7105, 일정명: '골든브릿지', 일정내용: '거대한 두 손이 다리를 받치고 있는 다낭의 상징입니다.', 시작시간: '13:00', 종료시간: '14:00' },
    { 일차: 3, 상품일정번호: 7106, 일정명: '호이안 올드타운', 일정내용: '유네스코 세계문화유산으로 지정된 옛 항구 도시입니다.', 시작시간: '09:00', 종료시간: '12:00' },
    { 일차: 3, 상품일정번호: 7107, 일정명: '투본강 소원배 (선택)', 일정내용: '해가 지면 등불을 띄웁니다. 선택 옵션입니다.', 시작시간: '18:30', 종료시간: '19:30' },
    { 일차: 4, 상품일정번호: 7108, 일정명: '자유일정', 일정내용: '리조트에서 쉬거나 시내로 나가실 수 있습니다.', 시작시간: '', 종료시간: '' },
    { 일차: 4, 상품일정번호: 7109, 일정명: '미케 비치 석양', 일정내용: '다낭에서 가장 유명한 해변입니다.', 시작시간: '17:00', 종료시간: '18:30' },
    { 일차: 5, 상품일정번호: 7110, 일정명: '다낭국제공항 출발', 일정내용: '체크아웃 후 공항으로 이동합니다.', 시작시간: '11:00', 종료시간: '14:15' },
    { 일차: 5, 상품일정번호: 7111, 일정명: '인천국제공항 도착', 일정내용: '해산합니다. 수고하셨습니다.', 시작시간: '21:00', 종료시간: '' },
  ],
  table3: [
    { 일차: 1, 식사정보: '조식 미제공 / 중식 기내식 / 석식 현지식' },
    { 일차: 2, 식사정보: '조식 호텔식 / 중식 현지식 / 석식 현지식' },
    { 일차: 3, 식사정보: '조식 호텔식 / 중식 현지식 / 석식 자유식' },
    { 일차: 4, 식사정보: '조식 호텔식 / 중식 자유식 / 석식 자유식' },
    { 일차: 5, 식사정보: '조식 호텔식 / 중식 기내식 / 석식 미제공' },
  ],
  table5: [
    { 일차: 1, 상품일정번호: 7201, 일정명: '다낭 데모 비치 리조트', 일정내용: '디럭스 오션뷰 · 2인 1실', 자원명: 'VN417 (오전출발)', 요금제명: '기본요금', 가격옵션명: '기본요금' },
    { 일차: 2, 상품일정번호: 7202, 일정명: '다낭 데모 비치 리조트', 일정내용: '디럭스 오션뷰 · 2인 1실', 자원명: 'VN417 (오전출발)', 요금제명: '기본요금', 가격옵션명: '기본요금' },
    { 일차: 3, 상품일정번호: 7203, 일정명: '다낭 데모 비치 리조트', 일정내용: '디럭스 오션뷰 · 2인 1실', 자원명: 'VN417 (오전출발)', 요금제명: '기본요금', 가격옵션명: '기본요금' },
    { 일차: 4, 상품일정번호: 7204, 일정명: '다낭 데모 비치 리조트', 일정내용: '디럭스 오션뷰 · 2인 1실', 자원명: 'VN417 (오전출발)', 요금제명: '기본요금', 가격옵션명: '기본요금' },
  ],
};

// ────────────────────────────────────────────────────────────────
// 투어 상품 목록 (DBO.TRP_S_투어상품리스트)
// ────────────────────────────────────────────────────────────────

/**
 * 목록 화면의 기본 필터는 "판매중 + 행사 있음" 이다.
 * 그래서 최종행사일이 미래인 상품이 최소 몇 개는 있어야 첫 화면이 비지 않는다.
 */
export const DEMO_PRODUCTS = [
  {
    상품번호: 990101,
    상품코드: 'DEMO-DAD-001',
    상품명: '[베스트셀러] 다낭 · 호이안 5일 — 4성 리조트 + 전일정 가이드',
    상품종류: '투어',
    상품종류코드: '001002',
    상품타입: '패키지',
    국가: '베트남',
    도시: '다낭',
    상품담당: '김데모',
    상품담당번호: '990001',
    예약담당: '이샘플',
    예약담당번호: '990002',
    판매여부: true,
    노출여부: true,
    여행기간: '4박 5일',
    소요: '박',
    소요시간: 5,
    최종행사일: dayFromNow(120),
    생성일시: dateTimeFromNow(-46, 10, 12),
    등록일시: dateTimeFromNow(-46, 10, 12),
    통화: '원화',
    통화코드: 'KRW',
    상품뱃지: 'BEST',
    상품비고: '리조트 업그레이드 프로모션 진행 중',
    키워드: '다낭,호이안,리조트,가족여행',
  },
  {
    상품번호: 990102,
    상품코드: 'DEMO-DAD-002',
    상품명: '다낭 자유여행 4일 — 항공 + 호텔만',
    상품종류: '투어',
    상품종류코드: '001002',
    상품타입: '자유여행',
    국가: '베트남',
    도시: '다낭',
    상품담당: '이샘플',
    상품담당번호: '990002',
    예약담당: '이샘플',
    예약담당번호: '990002',
    판매여부: true,
    노출여부: true,
    여행기간: '3박 4일',
    소요: '박',
    소요시간: 4,
    최종행사일: dayFromNow(95),
    생성일시: dateTimeFromNow(-31, 14, 5),
    등록일시: dateTimeFromNow(-31, 14, 5),
    통화: '원화',
    통화코드: 'KRW',
    상품뱃지: '',
    상품비고: '',
    키워드: '다낭,자유여행,에어텔',
  },
  {
    상품번호: 990103,
    상품코드: 'DEMO-BKK-001',
    상품명: '방콕 · 파타야 5일 — 노팁 노옵션',
    상품종류: '투어',
    상품종류코드: '001002',
    상품타입: '패키지',
    국가: '태국',
    도시: '방콕',
    상품담당: '박체험',
    상품담당번호: '990003',
    예약담당: '김데모',
    예약담당번호: '990001',
    판매여부: true,
    노출여부: true,
    여행기간: '4박 5일',
    소요: '박',
    소요시간: 5,
    최종행사일: dayFromNow(150),
    생성일시: dateTimeFromNow(-22, 9, 40),
    등록일시: dateTimeFromNow(-22, 9, 40),
    통화: '원화',
    통화코드: 'KRW',
    상품뱃지: 'HOT',
    상품비고: '',
    키워드: '방콕,파타야,노옵션',
  },
  {
    상품번호: 990104,
    상품코드: 'DEMO-OSA-001',
    상품명: '오사카 · 교토 3일 — 자유일정 + 공항픽업',
    상품종류: '투어',
    상품종류코드: '001002',
    상품타입: '자유여행',
    국가: '일본',
    도시: '오사카',
    상품담당: '김데모',
    상품담당번호: '990001',
    예약담당: '김데모',
    예약담당번호: '990001',
    판매여부: true,
    노출여부: true,
    여행기간: '2박 3일',
    소요: '박',
    소요시간: 3,
    최종행사일: dayFromNow(75),
    생성일시: dateTimeFromNow(-14, 16, 20),
    등록일시: dateTimeFromNow(-14, 16, 20),
    통화: '원화',
    통화코드: 'KRW',
    상품뱃지: '',
    상품비고: '벚꽃 시즌 좌석 선점 필요',
    키워드: '오사카,교토,자유여행',
  },
  {
    상품번호: 990105,
    상품코드: 'DEMO-DAD-003',
    상품명: '다낭 반나절 시내투어 (현지 출발)',
    상품종류: '투어',
    상품종류코드: '001002',
    상품타입: '현지투어',
    국가: '베트남',
    도시: '다낭',
    상품담당: '박체험',
    상품담당번호: '990003',
    예약담당: '박체험',
    예약담당번호: '990003',
    판매여부: true,
    노출여부: true,
    여행기간: '반일',
    소요: '시간',
    소요시간: 5,
    최종행사일: dayFromNow(200),
    생성일시: dateTimeFromNow(-8, 11, 0),
    등록일시: dateTimeFromNow(-8, 11, 0),
    통화: '원화',
    통화코드: 'KRW',
    상품뱃지: '',
    상품비고: '',
    키워드: '다낭,시내투어,반일',
  },
  {
    상품번호: 990106,
    상품코드: 'DEMO-PAR-001',
    상품명: '파리 · 근교 7일 (판매 중지 예시)',
    상품종류: '투어',
    상품종류코드: '001002',
    상품타입: '패키지',
    국가: '프랑스',
    도시: '파리',
    상품담당: '이샘플',
    상품담당번호: '990002',
    예약담당: '이샘플',
    예약담당번호: '990002',
    판매여부: false,
    노출여부: false,
    여행기간: '6박 7일',
    소요: '박',
    소요시간: 7,
    최종행사일: dayFromNow(-20), // 지난 행사 → 기본 필터에서는 안 보임
    생성일시: dateTimeFromNow(-120, 13, 45),
    등록일시: dateTimeFromNow(-120, 13, 45),
    통화: '원화',
    통화코드: 'KRW',
    상품뱃지: '',
    상품비고: '항공 스케줄 확정 전까지 판매 중지',
    키워드: '파리,유럽',
  },
];

/** 상품 상세(기초설정 모달)에서 추가로 필요한 값들 */
export const DEMO_PRODUCT_DETAIL_EXTRA = {
  상품코드룰사용: false,
  디폴트수배담당: '예약담당',
  소요타입: '박',
  가이드타입: '전일정 동행',
  가이드타입코드: '3001',
  카테고리: '해외패키지',
  카테고리코드: '4001',
  원가통화: 'KRW',
  체크인시간: '15:00',
  체크아웃시간: '11:00',
  예약확정방식: '자동확정',
  신청금: 300000,
  완납기준일: 21,
  마감기준일: 3,
};

// ────────────────────────────────────────────────────────────────
// 상품별 가격/재고 요약 (DBO.TRP_S_투어_판매가 등에서 재사용)
// ────────────────────────────────────────────────────────────────

export const DEMO_PRICE_ROWS = [
  { 출발일: dayFromNow(14), 성인가: 899000, 아동가: 799000, 유아가: 200000, 총좌석: 20, 예약: 12, 잔여: 8, 마감여부: false },
  { 출발일: dayFromNow(21), 성인가: 949000, 아동가: 849000, 유아가: 200000, 총좌석: 20, 예약: 4, 잔여: 16, 마감여부: false },
  { 출발일: dayFromNow(28), 성인가: 1099000, 아동가: 999000, 유아가: 200000, 총좌석: 24, 예약: 24, 잔여: 0, 마감여부: true },
  { 출발일: dayFromNow(35), 성인가: 899000, 아동가: 799000, 유아가: 200000, 총좌석: 20, 예약: 2, 잔여: 18, 마감여부: false },
];

// ────────────────────────────────────────────────────────────────
// 가격/재고 화면 (달력)
//
//   재고구분(자원) = 좌석을 나눠 담는 통. 보통 항공편 단위.
//   요금제        = 그 통에 매기는 가격표. 한 통에 여러 개 둘 수 있다.
//   좌석은 재고구분마다 따로 세고, 같은 통 안의 요금제들이 그 좌석을 나눠 쓴다.
// ────────────────────────────────────────────────────────────────

/** 재고구분 (DBO.RCT_S_투어상품자원리스트) */
export const DEMO_RESOURCES = [
  { 자원번호: 7001, 자원명: 'VN417 (오전출발)' },
  { 자원번호: 7002, 자원명: 'VN419 (저녁출발)' },
];

/** 재고구분별 요금제 (DBO.RCT_S_투어요금제리스트_02) */
export const DEMO_RATE_PLANS = {
  7001: [
    { 가격옵션번호: 8001, 요금제명: '기본요금', 가격정책: '인원별', 최소수량: 1, 최대수량: 20, 프라이빗: false, 환불불가: false, 자동확정: true, 주문수량제어: false },
    { 가격옵션번호: 8002, 요금제명: '제휴가', 가격정책: '인원별', 최소수량: 2, 최대수량: 20, 프라이빗: true, 환불불가: false, 자동확정: false, 주문수량제어: false },
  ],
  7002: [
    { 가격옵션번호: 8003, 요금제명: '기본요금', 가격정책: '인원별', 최소수량: 1, 최대수량: 15, 프라이빗: false, 환불불가: false, 자동확정: true, 주문수량제어: false },
  ],
};

/** 요금제별 기준 금액 (성인 / 소아 / 유아) 과 좌석 수 */
export const DEMO_PLAN_BASE = {
  8001: { 자원번호: 7001, sell: [1290000, 990000, 300000], tax: [150000, 150000, 50000], cost: [820000, 640000, 150000], seats: 20 },
  8002: { 자원번호: 7001, sell: [1150000, 890000, 300000], tax: [150000, 150000, 50000], cost: [820000, 640000, 150000], seats: 20 },
  8003: { 자원번호: 7002, sell: [1190000, 920000, 280000], tax: [140000, 140000, 50000], cost: [760000, 600000, 140000], seats: 15 },
};

/**
 * 공유재고 (DBO.TRP_S_공유재고)
 *
 * 여러 상품이 같은 좌석 묶음을 나눠 쓰는 기능이다. 예를 들어 항공 좌석 20석을
 * 하나 잡아 두고, 패키지 상품과 자유여행 상품이 그 20석을 함께 쓴다.
 * 이걸 쓰면 다른 상품에서 예약이 들어와도 이 상품의 잔여석이 같이 줄어든다.
 */
export const DEMO_SHARED_INVENTORY = [
  { 시리즈번호: 4101, 시리즈명: 'VN417 다낭 공동 좌석 (10~12월)', 공유색상: '#7c3aed', 종류: '항공', 편도: false, 공급사: '데모항공' },
  { 시리즈번호: 4102, 시리즈명: 'TG657 방콕 블록 좌석', 공유색상: '#0ea5e9', 종류: '항공', 편도: false, 공급사: '데모항공' },
  { 시리즈번호: 4103, 시리즈명: '다낭 리조트 객실 블록', 공유색상: '#10b981', 종류: '호텔', 편도: false, 공급사: '데모랜드' },
];

/** 출발 요일. 화요일(2)·금요일(5) 출발로 두면 달력이 실제 시리즈처럼 보인다. */
const DEPARTURE_DOWS = [2, 5];

/** 달력을 채울 최대 일수. 이보다 먼 미래는 "아직 안 연 기간"으로 비워 둔다. */
const CALENDAR_HORIZON_DAYS = 150;

const WON = '￦'; // ￦

function formatMoney(n) {
  return `${WON}${Number(n || 0).toLocaleString('ko-KR')}`;
}

function formatTriplet(values) {
  return values.map(formatMoney).join(' / ');
}

/** 날짜 문자열에서 뽑는 고정 난수. 새로고침해도 같은 값이 나와야 달력이 흔들리지 않는다. */
function seededInt(seed, max) {
  let h = 0;
  const s = String(seed);
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 100000;
  }
  return max > 0 ? h % max : 0;
}

function parseYMD(s) {
  const t = String(s || '').slice(0, 10);
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setHours(0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 달력 행 생성 (DBO.RCT_S_가격예약_02).
 *
 * overrides 는 데모에서 사용자가 저장한 값이다. 저장 → 달력에 바로 반영되는 느낌을
 * 내기 위해, 생성한 기본값 위에 덮어씌운다. 새로고침하면 사라진다.
 *   overrides.price[`${가격옵션번호}|${날짜}`] = { sell: [a,c,i], tax: [a,c,i] }
 *   overrides.stock[`${자원번호}|${날짜}`]     = 좌석수
 *   overrides.closed[`${자원번호}|${날짜}`]    = true | false
 */
export function buildPriceCalendarRows(startDate, endDate, overrides = {}) {
  const rangeStart = parseYMD(startDate);
  const rangeEnd = parseYMD(endDate);
  if (!rangeStart || !rangeEnd) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + CALENDAR_HORIZON_DAYS);

  const from = rangeStart > today ? rangeStart : today;
  const to = rangeEnd < horizon ? rangeEnd : horizon;

  const priceOverrides = overrides.price || {};
  const stockOverrides = overrides.stock || {};
  const closedOverrides = overrides.closed || {};

  const rows = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    if (DEPARTURE_DOWS.includes(cursor.getDay())) {
      const key = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(cursor.getDate())}`;

      DEMO_RESOURCES.forEach((resource) => {
        const resourceNo = resource.자원번호;
        const plans = DEMO_RATE_PLANS[resourceNo] || [];

        // 좌석과 예약 인원은 재고구분 단위다. 같은 통의 요금제들이 이 좌석을 나눠 쓴다.
        const firstPlan = plans[0];
        const baseSeats = firstPlan ? DEMO_PLAN_BASE[firstPlan.가격옵션번호].seats : 20;
        const seats = stockOverrides[`${resourceNo}|${key}`] ?? baseSeats;
        const sold = Math.min(seats, seededInt(`${resourceNo}${key}`, seats + 4));
        const closedOverride = closedOverrides[`${resourceNo}|${key}`];
        const closed = closedOverride != null ? !!closedOverride : sold >= seats;

        plans.forEach((plan) => {
          const planNo = plan.가격옵션번호;
          const base = DEMO_PLAN_BASE[planNo];
          const override = priceOverrides[`${planNo}|${key}`] || {};
          const sell = override.sell || base.sell;
          const tax = override.tax || base.tax;
          const total = sell.map((v, i) => Number(v || 0) + Number(tax[i] || 0));

          rows.push({
            행사시작일시: key,
            항공패턴: resource.자원명,
            자원번호: resourceNo,
            요금제: plan.요금제명,
            가격옵션번호: planNo,
            // 날짜별 가격 레코드 번호. 상세 조회·일괄 수정이 이 번호를 쓴다.
            가격번호: Number(`${planNo}${key.replace(/-/g, '').slice(4)}`),
            가격구분: plan.가격정책,
            원가방식: '항목별',
            총판매가: formatTriplet(total),
            판매가: formatTriplet(sell),
            Tax및유류할증료: formatTriplet(tax),
            커미션: formatTriplet([0, 0, 0]),
            원가: formatTriplet(base.cost),
            보유석: seats,
            잔여석: Math.max(0, seats - sold),
            모객수량: sold,
            마감여부: closed,
            출발확정: sold >= Math.ceil(seats / 2),
            일정확정: true,
            일정타입번호: 6001,
            일정표: '기본일정',
            기획전: '',
          });
        });
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return rows;
}

// ────────────────────────────────────────────────────────────────
// 예약 관리
//
// 예약은 화면이 한 장이 아니다. 예약조회 · 주문조회 · 단체조회 · 상품별 조회 · 예약 상세가
// 저마다 다른 프로시저를 부르지만, 결국 같은 예약을 다른 각도에서 볼 뿐이다.
// 그래서 원본은 아래 DEMO_ORDERS 한 벌만 적어 두고, 화면별 응답은 build* 함수가 그때그때 만든다.
// (화면마다 숫자가 어긋나면 데모라는 게 바로 티가 난다)
// ────────────────────────────────────────────────────────────────

/** 예약 상태 코드. ReservationManagement 의 statusDesign 과 코드가 같아야 색 뱃지가 붙는다. */
export const DEMO_SALE_STATUS = [
  { 코드: '049001', 코드명: '예약접수' },
  { 코드: '049002', 코드명: '변경접수' },
  { 코드: '049011', 코드명: '예약확정' },
  { 코드: '049019', 코드명: '최종확정' },
  { 코드: '049021', 코드명: '취소접수' },
  { 코드: '049022', 코드명: '취소확정' },
  { 코드: '049029', 코드명: '취소완료' },
  { 코드: '049031', 코드명: '예약불가' },
];

function statusNameOf(code) {
  const hit = DEMO_SALE_STATUS.find((s) => s.코드 === String(code));
  return hit ? hit.코드명 : '';
}

/** 여행객 한 명. 여권까지 채워 두는 이유는, 비어 있으면 "예약이 덜 들어왔다"로 오해되기 때문이다. */
function makeTraveler(한글이름, 성별, 영문성, 영문이름, 생년월일, 여권번호, extra = {}) {
  return {
    한글이름,
    성별,
    영문성,
    영문이름,
    생년월일,
    여권번호,
    여권만료일: extra.여권만료일 || dayFromNow(900),
    연령구분: extra.연령구분 || '성인',
    여행객비고: extra.여행객비고 || '',
    휴대폰: extra.휴대폰 || '',
    이메일: extra.이메일 || '',
  };
}

/** 상품별 1인 단가. [상품금액, 택스및유류] 순서이고 연령구분에 따라 값이 갈린다. */
const 다낭5일단가 = { 성인: [899000, 180000], 아동: [799000, 180000], 유아: [150000, 0] };
const 다낭자유단가 = { 성인: [649000, 150000], 아동: [599000, 150000], 유아: [100000, 0] };
const 방콕단가 = { 성인: [799000, 170000], 아동: [719000, 170000], 유아: [120000, 0] };
const 오사카단가 = { 성인: [590000, 120000], 아동: [530000, 120000], 유아: [90000, 0] };
const 시내투어단가 = { 성인: [45000, 0], 아동: [35000, 0], 유아: [0, 0] };

/**
 * 데모 주문 목록.
 *   입금: 'full' 완납 / 'part' 신청금만 / 'none' 미입금
 *   대리점번호가 비어 있으면 우리가 직접 판 것(=직판)이다.
 */
export const DEMO_ORDERS = [
  {
    주문번호: 26000101,
    판매번호: 9901001,
    상품번호: 990101,
    상품코드: 'DEMO-DAD-001',
    상품명: '[베스트셀러] 다낭 · 호이안 5일 — 4성 리조트 + 전일정 가이드',
    국가: '베트남',
    도시: '다낭',
    수배담당자: '김데모',
    예약담당자: '이샘플',
    예약일오프셋: -20,
    예약시각: [10, 22],
    출발오프셋: 5,
    일수: 5,
    단가: 다낭5일단가,
    입금: 'full',
    예약상태코드: '049019',
    주문경로: '전화 문의',
    대리점: '',
    대리점번호: '',
    행사단체번호: '880101',
    행사단체명: '다낭 · 호이안 5일 1진',
    판매비고: '리조트 오션뷰 요청 접수함. 현지 컨펌 완료.',
    예약자: { 이름: '홍길동', 휴대폰: '010-1111-2222', 이메일: 'demo-hong@example.com' },
    여행객: [
      makeTraveler('홍길동', '남', 'HONG', 'GILDONG', '1982-03-14', 'M12345678', { 휴대폰: '010-1111-2222', 이메일: 'demo-hong@example.com' }),
      makeTraveler('김서연', '여', 'KIM', 'SEOYEON', '1984-07-02', 'M12345679'),
      makeTraveler('홍지우', '여', 'HONG', 'JIWOO', '2011-05-20', 'M12345680', { 연령구분: '아동', 여행객비고: '기내식 알레르기(견과류)' }),
      makeTraveler('홍민준', '남', 'HONG', 'MINJUN', '2016-09-08', 'M12345681', { 연령구분: '아동' }),
    ],
  },
  {
    주문번호: 26000102,
    판매번호: 9901002,
    상품번호: 990101,
    상품코드: 'DEMO-DAD-001',
    상품명: '[베스트셀러] 다낭 · 호이안 5일 — 4성 리조트 + 전일정 가이드',
    국가: '베트남',
    도시: '다낭',
    수배담당자: '김데모',
    예약담당자: '이샘플',
    예약일오프셋: -12,
    예약시각: [15, 8],
    출발오프셋: 5,
    일수: 5,
    단가: 다낭5일단가,
    입금: 'part',
    예약상태코드: '049011',
    주문경로: '대리점 접수',
    대리점: '하나로투어 강남지점',
    대리점번호: '88001',
    행사단체번호: '880101',
    행사단체명: '다낭 · 호이안 5일 1진',
    판매비고: '잔금은 출발 7일 전까지 입금 예정',
    예약자: { 이름: '김여행', 휴대폰: '010-3333-4444', 이메일: 'demo-kim@example.com' },
    여행객: [
      makeTraveler('김여행', '남', 'KIM', 'YEOHAENG', '1979-11-30', 'M22345678', { 휴대폰: '010-3333-4444' }),
      makeTraveler('박은정', '여', 'PARK', 'EUNJEONG', '1981-02-17', 'M22345679', { 여행객비고: '부부 트윈 → 더블 변경 요청' }),
    ],
  },
  {
    주문번호: 26000103,
    판매번호: 9901003,
    상품번호: 990103,
    상품코드: 'DEMO-BKK-001',
    상품명: '방콕 · 파타야 5일 — 노팁 노옵션',
    국가: '태국',
    도시: '방콕',
    수배담당자: '박체험',
    예약담당자: '김데모',
    예약일오프셋: -9,
    예약시각: [11, 47],
    출발오프셋: 12,
    일수: 5,
    단가: 방콕단가,
    입금: 'part',
    예약상태코드: '049011',
    주문경로: '홈페이지',
    대리점: '',
    대리점번호: '',
    행사단체번호: '880104',
    행사단체명: '방콕 · 파타야 5일 1진',
    판매비고: '',
    예약자: { 이름: '박손님', 휴대폰: '010-5555-6666', 이메일: 'demo-park@example.com' },
    여행객: [
      makeTraveler('박손님', '남', 'PARK', 'SONNIM', '1990-06-11', 'M32345678', { 휴대폰: '010-5555-6666' }),
      makeTraveler('이하늘', '여', 'LEE', 'HANEUL', '1992-01-25', 'M32345679'),
    ],
  },
  {
    주문번호: 26000104,
    판매번호: 9901004,
    상품번호: 990104,
    상품코드: 'DEMO-OSA-001',
    상품명: '오사카 · 교토 3일 — 자유일정 + 공항픽업',
    국가: '일본',
    도시: '오사카',
    수배담당자: '김데모',
    예약담당자: '김데모',
    예약일오프셋: -5,
    예약시각: [9, 3],
    출발오프셋: 18,
    일수: 3,
    단가: 오사카단가,
    입금: 'full',
    예약상태코드: '049011',
    주문경로: '대리점 접수',
    대리점: '더드림투어',
    대리점번호: '88002',
    행사단체번호: '880105',
    행사단체명: '오사카 · 교토 3일 1진',
    판매비고: '허니문 — 호텔 축하 세팅 요청',
    예약자: { 이름: '최지은', 휴대폰: '010-7777-8888', 이메일: 'demo-choi@example.com' },
    여행객: [
      makeTraveler('최지은', '여', 'CHOI', 'JIEUN', '1994-04-09', 'M42345678', { 휴대폰: '010-7777-8888' }),
      makeTraveler('윤도현', '남', 'YOON', 'DOHYUN', '1993-12-01', 'M42345679', { 여행객비고: '신혼여행 / 늦은 체크인' }),
    ],
  },
  {
    주문번호: 26000105,
    판매번호: 9901005,
    상품번호: 990102,
    상품코드: 'DEMO-DAD-002',
    상품명: '다낭 자유여행 4일 — 항공 + 호텔만',
    국가: '베트남',
    도시: '다낭',
    수배담당자: '이샘플',
    예약담당자: '이샘플',
    예약일오프셋: -3,
    예약시각: [20, 36],
    출발오프셋: 23,
    일수: 4,
    단가: 다낭자유단가,
    입금: 'none',
    예약상태코드: '049001',
    주문경로: '홈페이지',
    대리점: '',
    대리점번호: '',
    행사단체번호: '880106',
    행사단체명: '다낭 자유여행 4일 1진',
    판매비고: '신청금 미입금 — 3일 내 미입금 시 자동 취소 안내함',
    예약자: { 이름: '정수민', 휴대폰: '010-9999-0000', 이메일: 'demo-jung@example.com' },
    여행객: [
      makeTraveler('정수민', '여', 'JUNG', 'SUMIN', '1988-08-19', 'M52345678', { 휴대폰: '010-9999-0000' }),
      makeTraveler('한지훈', '남', 'HAN', 'JIHUN', '1987-10-05', 'M52345679'),
      makeTraveler('한서윤', '여', 'HAN', 'SEOYUN', dayFromNow(-500), 'M52345680', { 연령구분: '유아', 여행객비고: '유아 좌석 없음 / 유모차 반입' }),
    ],
  },
  {
    주문번호: 26000106,
    판매번호: 9901006,
    상품번호: 990105,
    상품코드: 'DEMO-DAD-003',
    상품명: '다낭 반나절 시내투어 (현지 출발)',
    국가: '베트남',
    도시: '다낭',
    수배담당자: '박체험',
    예약담당자: '박체험',
    예약일오프셋: -2,
    예약시각: [13, 15],
    출발오프셋: 9,
    일수: 1,
    단가: 시내투어단가,
    입금: 'full',
    예약상태코드: '049011',
    주문경로: '제휴몰',
    대리점: '',
    대리점번호: '',
    행사단체번호: '880107',
    행사단체명: '다낭 반나절 시내투어 1진',
    판매비고: '호텔 픽업 08:30 — 로비 집합',
    예약자: { 이름: '오현우', 휴대폰: '010-2222-3333', 이메일: 'demo-oh@example.com' },
    여행객: [
      makeTraveler('오현우', '남', 'OH', 'HYUNWOO', '1975-05-05', 'M62345678', { 휴대폰: '010-2222-3333' }),
      makeTraveler('신미경', '여', 'SHIN', 'MIKYUNG', '1977-03-21', 'M62345679'),
      makeTraveler('오세진', '남', 'OH', 'SEJIN', '2005-07-14', 'M62345680'),
      makeTraveler('오다인', '여', 'OH', 'DAIN', '2009-11-02', 'M62345681', { 연령구분: '아동' }),
      makeTraveler('강복순', '여', 'KANG', 'BOKSOON', '1950-01-08', 'M62345682', { 여행객비고: '보행 불편 — 앞자리 배정' }),
      makeTraveler('오정호', '남', 'OH', 'JEONGHO', '1948-04-27', 'M62345683'),
    ],
  },
  {
    주문번호: 26000107,
    판매번호: 9901007,
    상품번호: 990103,
    상품코드: 'DEMO-BKK-001',
    상품명: '방콕 · 파타야 5일 — 노팁 노옵션',
    국가: '태국',
    도시: '방콕',
    수배담당자: '박체험',
    예약담당자: '김데모',
    예약일오프셋: -15,
    예약시각: [16, 40],
    출발오프셋: 12,
    일수: 5,
    단가: 방콕단가,
    입금: 'none',
    취소료: 100000,
    예약상태코드: '049029',
    주문경로: '대리점 접수',
    대리점: '위투어 부산',
    대리점번호: '88003',
    행사단체번호: '880104',
    행사단체명: '방콕 · 파타야 5일 1진',
    판매비고: '고객 사정으로 취소. 취소료 10만원 청구 완료.',
    예약자: { 이름: '강민호', 휴대폰: '010-4444-5555', 이메일: 'demo-kang@example.com' },
    여행객: [
      makeTraveler('강민호', '남', 'KANG', 'MINHO', '1985-09-17', 'M72345678', { 휴대폰: '010-4444-5555' }),
      makeTraveler('조유진', '여', 'CHO', 'YUJIN', '1986-12-29', 'M72345679'),
    ],
  },
  {
    주문번호: 26000108,
    판매번호: 9901008,
    상품번호: 990104,
    상품코드: 'DEMO-OSA-001',
    상품명: '오사카 · 교토 3일 — 자유일정 + 공항픽업',
    국가: '일본',
    도시: '오사카',
    수배담당자: '김데모',
    예약담당자: '김데모',
    예약일오프셋: -1,
    예약시각: [18, 2],
    출발오프셋: 18,
    일수: 3,
    단가: 오사카단가,
    입금: 'part',
    예약상태코드: '049002',
    주문경로: '홈페이지',
    대리점: '',
    대리점번호: '',
    행사단체번호: '880105',
    행사단체명: '오사카 · 교토 3일 1진',
    판매비고: '인원 3명 → 4명 변경 요청 접수, 항공 좌석 확인 중',
    예약자: { 이름: '서예린', 휴대폰: '010-6666-7777', 이메일: 'demo-seo@example.com' },
    여행객: [
      makeTraveler('서예린', '여', 'SEO', 'YERIN', '1996-02-14', 'M82345678', { 휴대폰: '010-6666-7777' }),
      makeTraveler('배준영', '남', 'BAE', 'JUNYOUNG', '1995-08-23', 'M82345679'),
      makeTraveler('노가은', '여', 'NOH', 'GAEUN', '1997-06-30', 'M82345680'),
      makeTraveler('임세훈', '남', 'LIM', 'SEHUN', '1994-10-11', 'M82345681', { 여행객비고: '추가 인원 — 여권 사본 미제출' }),
    ],
  },
  {
    주문번호: 26000109,
    판매번호: 9901009,
    상품번호: 990101,
    상품코드: 'DEMO-DAD-001',
    상품명: '[베스트셀러] 다낭 · 호이안 5일 — 4성 리조트 + 전일정 가이드',
    국가: '베트남',
    도시: '다낭',
    수배담당자: '김데모',
    예약담당자: '이샘플',
    예약일오프셋: -30,
    예약시각: [9, 51],
    출발오프셋: 2,
    일수: 5,
    단가: 다낭5일단가,
    입금: 'full',
    예약상태코드: '049019',
    주문경로: '전화 문의',
    대리점: '',
    대리점번호: '',
    행사단체번호: '880102',
    행사단체명: '다낭 · 호이안 5일 2진',
    판매비고: '출발 임박 — 바우처 발송 완료',
    예약자: { 이름: '문가영', 휴대폰: '010-8888-9999', 이메일: 'demo-moon@example.com' },
    여행객: [
      makeTraveler('문가영', '여', 'MOON', 'GAYOUNG', '1991-01-19', 'M92345678', { 휴대폰: '010-8888-9999' }),
      makeTraveler('류시원', '남', 'RYU', 'SIWON', '1989-05-06', 'M92345679'),
    ],
  },
  {
    주문번호: 26000110,
    판매번호: 9901010,
    상품번호: 990101,
    상품코드: 'DEMO-DAD-001',
    상품명: '[베스트셀러] 다낭 · 호이안 5일 — 4성 리조트 + 전일정 가이드',
    국가: '베트남',
    도시: '다낭',
    수배담당자: '이샘플',
    예약담당자: '이샘플',
    예약일오프셋: 0,
    예약시각: [8, 5],
    출발오프셋: 45,
    일수: 5,
    단가: 다낭5일단가,
    입금: 'none',
    예약상태코드: '049001',
    주문경로: '대리점 접수',
    대리점: '하나로투어 강남지점',
    대리점번호: '88001',
    행사단체번호: '880103',
    행사단체명: '다낭 · 호이안 5일 3진',
    판매비고: '기본 조회기간(한 달) 밖이라, 기간을 넓혀야 보이는 예약',
    예약자: { 이름: '임태호', 휴대폰: '010-1212-3434', 이메일: 'demo-lim@example.com' },
    여행객: [
      makeTraveler('임태호', '남', 'LIM', 'TAEHO', '1983-07-07', 'MA2345678', { 휴대폰: '010-1212-3434' }),
      makeTraveler('구본영', '남', 'KOO', 'BONYOUNG', '1984-09-13', 'MA2345679'),
    ],
  },
];

// ── 파생 계산 ────────────────────────────────────────────────

/** 여행객 한 명의 상품금액 / 택스및유류 */
function travelerPrice(order, traveler) {
  const row = (order.단가 || {})[traveler.연령구분] || [0, 0];
  return { 상품금액: row[0], 택스및유류: row[1] };
}

function isCancelledOrder(order) {
  return ['049021', '049022', '049029'].includes(String(order.예약상태코드));
}

/** 주문 한 건의 합계. 화면마다 따로 계산하면 숫자가 어긋나므로 여기 한 곳에만 둔다. */
export function orderTotals(order) {
  const travelers = order.여행객 || [];
  const 상품금액 = travelers.reduce((sum, t) => sum + travelerPrice(order, t).상품금액, 0);
  const 택스및유류 = travelers.reduce((sum, t) => sum + travelerPrice(order, t).택스및유류, 0);
  const 총액 = 상품금액 + 택스및유류;

  // 신청금은 1인 20만원으로 잡는다. 실제 여행사들이 흔히 쓰는 금액이다.
  let 입금액 = 0;
  if (order.입금 === 'full') 입금액 = 총액;
  else if (order.입금 === 'part') 입금액 = Math.min(총액, travelers.length * 200000);

  return {
    인원: travelers.length,
    상품금액,
    택스및유류,
    총액,
    입금액,
    미수액: Math.max(0, 총액 - 입금액),
    취소료: Number(order.취소료) || 0,
  };
}

function orderDates(order) {
  const [hh, mm] = order.예약시각 || [9, 30];
  return {
    예약일: dateTimeFromNow(order.예약일오프셋, hh, mm),
    예약일자: dayFromNow(order.예약일오프셋),
    행사시작일: dayFromNow(order.출발오프셋),
    행사종료일: dayFromNow(order.출발오프셋 + Math.max(0, (order.일수 || 1) - 1)),
  };
}

/** 'YYYY-MM-DD...' 문자열이 [start, end] 안에 드는지 */
function withinRange(value, start, end) {
  const d = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  if (start && d < String(start).slice(0, 10)) return false;
  if (end && d > String(end).slice(0, 10)) return false;
  return true;
}

/**
 * 조회기간에 걸리는 주문만 고른다.
 * 검색기준 '1' 은 예약일, '2'(기본) 는 행사일 기준이다. 화면의 [조회구분] 이 이 값이다.
 */
export function filterOrdersByPeriod({ start, end, basis } = {}) {
  if (!start && !end) return DEMO_ORDERS.slice();
  return DEMO_ORDERS.filter((order) => {
    const dates = orderDates(order);
    const target = String(basis) === '1' ? dates.예약일자 : dates.행사시작일;
    return withinRange(target, start, end);
  });
}

export function findDemoOrder(orderNo) {
  const key = String(orderNo ?? '').trim();
  if (!key) return null;
  return DEMO_ORDERS.find((o) => String(o.주문번호) === key) || null;
}

export function findDemoOrderBySaleNo(saleNo) {
  const key = String(saleNo ?? '').trim();
  if (!key) return null;
  return DEMO_ORDERS.find((o) => String(o.판매번호) === key) || null;
}

// ── 화면별 응답 만들기 ───────────────────────────────────────

/**
 * 예약조회 탭 (WSP_S_예약현황_여행객리스트).
 * 한 줄이 예약 한 건이 아니라 "여행객 한 명" 이다. 같은 주문의 여행객끼리 묶여서 보인다.
 */
export function buildTravelerListRows({ start, end, basis } = {}) {
  const rows = [];
  filterOrdersByPeriod({ start, end, basis }).forEach((order) => {
    const dates = orderDates(order);
    const totals = orderTotals(order);
    const cancelled = isCancelledOrder(order);
    const 입금상태 = totals.입금액 <= 0 ? '미입금' : totals.미수액 > 0 ? '부분입금' : '완납';

    (order.여행객 || []).forEach((t, idx) => {
      const price = travelerPrice(order, t);
      rows.push({
        주문번호: order.주문번호,
        판매번호: order.판매번호,
        주문판매번호: order.판매번호,
        상품번호: order.상품번호,
        상품코드: order.상품코드,
        판매상품명: order.상품명,
        예약일: dates.예약일,
        행사시작일: dates.행사시작일,
        행사종료일: dates.행사종료일,
        국가: order.국가,
        도시: order.도시,
        수배담당자: order.수배담당자,
        예약담당자: order.예약담당자,
        대리점: order.대리점,
        대리점번호: order.대리점번호,
        예약상태: statusNameOf(order.예약상태코드),
        예약상태코드: order.예약상태코드,
        입금상태,
        상품금액: price.상품금액,
        택스및유류: price.택스및유류,
        여행객번호: order.판매번호 * 100 + idx + 1,
        여행객종류: cancelled ? '취소' : '예약',
        연령구분: t.연령구분,
        한글이름: t.한글이름,
        성별: t.성별,
        영문성: t.영문성,
        영문이름: t.영문이름,
        생년월일: t.생년월일,
        여권번호: t.여권번호,
        여권만료일: t.여권만료일,
        여행객비고: t.여행객비고,
        휴대폰: t.휴대폰 || order.예약자.휴대폰,
        이메일: t.이메일 || order.예약자.이메일,
        행사단체번호: order.행사단체번호,
        행사단체명: order.행사단체명,
        판매비고: order.판매비고,
      });
    });
  });
  return rows;
}

/**
 * 주문조회 · 상품별 조회 · 실적 통계 탭 (WSP_S_예약현황_02).
 * 여기서는 한 줄이 판매(=주문) 한 건이다.
 */
export function buildOrderStatusRows({ start, end, basis, productNo } = {}) {
  return filterOrdersByPeriod({ start, end, basis })
    // 화면에 따라 상품번호로도 오고 상품코드로도 온다(목록의 [주문내역] 버튼). 둘 다 받는다.
    .filter((order) => {
      if (productNo == null || productNo === '') return true;
      const key = String(productNo);
      return String(order.상품번호) === key || String(order.상품코드) === key;
    })
    .map((order) => {
      const dates = orderDates(order);
      const totals = orderTotals(order);
      const 입금상태 = totals.입금액 <= 0 ? '미입금' : totals.미수액 > 0 ? '부분입금' : '입금완료';
      const 직판 = !order.대리점번호;

      return {
        주문번호: order.주문번호,
        판매번호: order.판매번호,
        주문판매번호: order.판매번호,
        예약일: dates.예약일,
        행사시작일: dates.행사시작일,
        행사시작일시: dates.행사시작일,
        행사종료일시: dates.행사종료일,
        종류: '투어',
        상품번호: order.상품번호,
        상품코드: order.상품코드,
        상품명: order.상품명,
        국가: order.국가,
        도시: order.도시,
        수배담당자: order.수배담당자,
        상품담당: order.수배담당자,
        예약담당자: order.예약담당자,
        예약고객명: order.예약자.이름,
        고객명: order.예약자.이름,
        고객핸드폰: order.예약자.휴대폰,
        수량: totals.인원,
        상품금액: totals.총액,
        입금액: totals.입금액,
        미수액: totals.미수액,
        판매가: totals.총액,
        미수금: totals.미수액,
        예약상태: statusNameOf(order.예약상태코드),
        예약상태2: statusNameOf(order.예약상태코드),
        예약상태코드: order.예약상태코드,
        판매상태: statusNameOf(order.예약상태코드),
        입금상태,
        취소여부: isCancelledOrder(order) ? '1' : '0',
        판매처: 직판 ? DEMO_COMPANY.업체명 : order.대리점,
        판매처번호: 직판 ? DEMO_COMPANY_NO : order.대리점번호,
        판매업체명: 직판 ? DEMO_COMPANY.업체명 : order.대리점,
        판매업체번호: 직판 ? DEMO_COMPANY_NO : order.대리점번호,
        대표공급업체명: DEMO_COMPANY.업체명,
        대표판매상품: order.상품명,
        대리점: order.대리점,
        행사단체번호: order.행사단체번호,
        행사단체명: order.행사단체명,
        주문경로: order.주문경로,
        주문일시: dates.예약일,
        출발일: dates.행사시작일,
        판매비고: order.판매비고,
      };
    });
}

/**
 * 단체조회 탭 (WSP_S_행사단체결산_메인).
 * 화면이 행사단체번호로 다시 묶는데, 건수·인원·최종수익은 첫 줄 값을 그대로 쓰고
 * 금액만 줄마다 더한다. 그래서 건수·인원은 단체 합계를 모든 줄에 같이 박아 둔다.
 */
export function buildGroupSettlementRows({ start, end } = {}) {
  const orders = filterOrdersByPeriod({ start, end, basis: '2' });

  const groups = new Map();
  orders.forEach((order) => {
    const key = String(order.행사단체번호);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(order);
  });

  const rows = [];
  groups.forEach((members) => {
    const live = members.filter((o) => !isCancelledOrder(o));
    const 판매건수 = live.length;
    const 취소건수 = members.length - live.length;
    const 행사인원 = live.reduce((sum, o) => sum + orderTotals(o).인원, 0);
    const netTotal = live.reduce((sum, o) => sum + orderTotals(o).총액, 0);
    // 전도금(랜드사에 지급하는 원가)은 판매가의 65% 정도로 잡는다. 데모용 어림값이다.
    const costTotal = Math.round((netTotal * 0.65) / 1000) * 1000;
    const 최종수익 = netTotal - costTotal;
    const 대표 = members[0];
    const 대표일정 = orderDates(대표);

    members.forEach((order) => {
      const totals = orderTotals(order);
      const cancelled = isCancelledOrder(order);
      const 판매NET = cancelled ? 0 : totals.총액;
      const cost = cancelled ? 0 : Math.round((판매NET * 0.65) / 1000) * 1000;
      const dates = orderDates(order);

      rows.push({
        행사단체번호: order.행사단체번호,
        행사단체명: order.행사단체명,
        종류: '투어',
        수배담당자: order.수배담당자,
        예약담당자: order.예약담당자,
        도시: order.도시,
        국가: order.국가,
        시작일: dates.행사시작일,
        종료일: dates.행사종료일,
        행사시작일시: 대표일정.행사시작일,
        행사종료일시: 대표일정.행사종료일,
        판매건수,
        취소건수,
        행사인원2: 행사인원,
        행사인원: 행사인원,
        판매현황NET: 판매NET,
        판매현황입금액: cancelled ? 0 : totals.입금액,
        판매현황미수액: cancelled ? 0 : totals.미수액,
        원화환산액: cost,
        출금액: cost,
        미지급액: 0,
        수익합계: 판매NET - cost,
        최종수익,
        예약상태명: statusNameOf(대표.예약상태코드),
        마감상태명: '미마감',
        판매번호: order.판매번호,
        주문번호: order.주문번호,
        대리점: order.대리점,
      });
    });
  });

  return rows;
}

/** 실적 통계의 미수 현황 (TRP_S_미수현황) */
export function buildOutstandingRows({ start, end } = {}) {
  return filterOrdersByPeriod({ start, end, basis: '2' })
    .filter((order) => !isCancelledOrder(order) && orderTotals(order).미수액 > 0)
    .map((order) => {
      const dates = orderDates(order);
      return {
        행사일시: dates.행사시작일,
        행사단체명: order.행사단체명,
        예약담당자: order.예약담당자,
        예약상태명: statusNameOf(order.예약상태코드),
        미수금액: orderTotals(order).미수액,
        입금상태: '미수',
        주문번호: order.주문번호,
        판매번호: order.판매번호,
      };
    });
}

// ── 예약 상세 화면 ───────────────────────────────────────────

/** 주문정보 (TRP_S_주문조회_주문정보) */
export function buildOrderInfo(order) {
  const dates = orderDates(order);
  const totals = orderTotals(order);
  const 직판 = !order.대리점번호;
  return {
    주문번호: order.주문번호,
    주문일: dates.예약일,
    주문자: order.예약자.이름,
    주문자휴대폰: order.예약자.휴대폰,
    주문자이메일: order.예약자.이메일,
    이메일: order.예약자.이메일,
    주문경로: order.주문경로,
    사이트: 직판 ? '자사몰' : order.대리점,
    대리점: order.대리점,
    대리점명: order.대리점,
    대리점번호: order.대리점번호,
    대리점코드: order.대리점번호,
    판매상품명: order.상품명,
    판매번호: order.판매번호,
    주문판매번호: order.판매번호,
    판매업체명: DEMO_COMPANY.업체명,
    판매업체번호: DEMO_COMPANY_NO,
    결제금액: totals.입금액,
    상품번호: order.상품번호,
    상품코드: order.상품코드,
  };
}

/** 판매상태 (TRP_S_주문관리_판매리스트) */
export function buildSaleStatusRow(order) {
  return {
    판매번호: order.판매번호,
    주문판매번호: order.판매번호,
    주문번호: order.주문번호,
    판매상태: statusNameOf(order.예약상태코드),
    판매상태코드: order.예약상태코드,
    취소여부: isCancelledOrder(order) ? '1' : '0',
  };
}

/** 가격정보 (TRP_S_예약관리_가격정보) */
export function buildPriceInfo(order) {
  const totals = orderTotals(order);
  // 대리점을 통해 들어온 예약에는 커미션이 붙는다. 넷가 = 고객결제가 - 커미션.
  const 커미션 = order.대리점번호 ? Math.round((totals.총액 * 0.07) / 1000) * 1000 : 0;
  return {
    판매번호: order.판매번호,
    상품금액원화: totals.상품금액,
    택스및유류원화: totals.택스및유류,
    취소료원화: totals.취소료,
    프로모션할인원화: 0,
    기타할인원화: 0,
    대리점커미션원화: 커미션,
    고객결제가원화: totals.총액,
    넷가원화: totals.총액 - 커미션,
  };
}

/** 예약 상세 · 여행객 명단 팝오버의 여행객 목록 (CW_S_예약관리_여행객리스트) */
export function buildTravelerDetailRows(order) {
  const cancelled = isCancelledOrder(order);
  const dates = orderDates(order);
  return (order.여행객 || []).map((t, idx) => {
    const price = travelerPrice(order, t);
    const no = order.판매번호 * 100 + idx + 1;
    return {
      여행객번호: no,
      여행객키: String(no),
      판매번호: order.판매번호,
      주문번호: order.주문번호,
      // 룸번호 인쇄물의 머리말이 이 날짜를 쓴다.
      판매출발일: dates.행사시작일,
      순번: idx + 1,
      여행객종류: cancelled ? '취소' : '예약',
      연령구분: t.연령구분,
      한글이름: t.한글이름,
      성별: t.성별,
      영문성: t.영문성,
      영문이름: t.영문이름,
      생년월일: t.생년월일,
      여권번호: t.여권번호,
      여권만료일: t.여권만료일,
      여행객비고: t.여행객비고,
      휴대폰: t.휴대폰 || order.예약자.휴대폰,
      이메일: t.이메일 || order.예약자.이메일,
      룸번호: '',
      상품금액원화: price.상품금액,
      택스및유류원화: price.택스및유류,
    };
  });
}

/** 입금내역 (WSP_S_입금리스트). list 는 표, summary 는 위쪽 요약 숫자다. */
export function buildDepositData(order) {
  const totals = orderTotals(order);
  const list = [];

  if (totals.입금액 > 0) {
    const 신청금 = Math.min(totals.입금액, (order.여행객 || []).length * 200000);
    list.push({
      결제번호: order.판매번호 * 10 + 1,
      수납일: dayFromNow(order.예약일오프셋),
      송금카드소유자: order.예약자.이름,
      결제구분: '계좌이체(신청금)',
      입금액: 신청금,
      비고: '예약금 입금 확인',
    });
    const 잔금 = totals.입금액 - 신청금;
    if (잔금 > 0) {
      list.push({
        결제번호: order.판매번호 * 10 + 2,
        수납일: dayFromNow(Math.min(0, order.예약일오프셋 + 7)),
        송금카드소유자: order.예약자.이름,
        결제구분: '계좌이체(잔금)',
        입금액: 잔금,
        비고: '잔금 완납',
      });
    }
  }

  return {
    list,
    summary: { 입금액: totals.입금액, 미수금: totals.미수액, 총판매가: totals.총액 },
  };
}

// ────────────────────────────────────────────────────────────────
// 예약 상세 · 하위 기능
//
// 예약 상세는 "예약 한 건을 끝까지 처리하는" 화면이다.
// 주문·상태·가격·여행객·입금만 채워 두면 조회는 되지만, 정작 예약을 받은 다음에 하는 일
// (돈이 들어왔는지 맞춰 보고 · 방을 배정하고 · 취소를 처리하고 · 그 과정을 기록으로 남기는 일)
// 이 데모에서 끊긴다. 아래는 그 끊긴 자리를 메우는 원본 데이터다.
// ────────────────────────────────────────────────────────────────

/**
 * 취소 수수료 규정.
 * 출발이 가까울수록 위약금이 커진다. 실제 여행사 약관을 데모용으로 단순화한 것이다.
 * 남은 날짜가 큰 것부터 적어야 한다 (cancelPolicyFor 가 위에서부터 첫 번째로 걸리는 줄을 쓴다).
 */
export const DEMO_CANCEL_POLICY = [
  { 남은일: 30, 비율: 0, 설명: '출발 30일 전까지 — 위약금 없음' },
  { 남은일: 20, 비율: 0.1, 설명: '출발 20~29일 전 — 상품가의 10%' },
  { 남은일: 10, 비율: 0.2, 설명: '출발 10~19일 전 — 상품가의 20%' },
  { 남은일: 8, 비율: 0.3, 설명: '출발 8~9일 전 — 상품가의 30%' },
  { 남은일: 3, 비율: 0.5, 설명: '출발 3~7일 전 — 상품가의 50%' },
  { 남은일: 1, 비율: 0.7, 설명: '출발 1~2일 전 — 상품가의 70%' },
  { 남은일: -99999, 비율: 1, 설명: '출발 당일 이후 — 환불 불가' },
];

/** 출발까지 남은 날짜에 해당하는 취소 규정 한 줄 */
export function cancelPolicyFor(daysLeft) {
  const n = Number(daysLeft);
  const days = Number.isFinite(n) ? n : 0;
  return DEMO_CANCEL_POLICY.find((p) => days >= p.남은일) || DEMO_CANCEL_POLICY[DEMO_CANCEL_POLICY.length - 1];
}

/**
 * 취소 처리 모달을 열 때 부르는 조회 (TRP_S_예약상태및취소료가져오기).
 * 화면은 이 중 취소료만 읽어서 입력칸의 초깃값으로 쓴다. 나머지는 데모에서 숫자를 설명하기 위한 값이다.
 */
export function buildCancelInfo(order) {
  const totals = orderTotals(order);
  const daysLeft = Number(order.출발오프셋) || 0;
  const policy = cancelPolicyFor(daysLeft);
  // 천 원 단위로 끊는다. 실제 여행사도 원 단위로 청구하지 않는다.
  const 취소료 = Math.round((totals.총액 * policy.비율) / 1000) * 1000;
  return {
    판매번호: order.판매번호,
    주문번호: order.주문번호,
    예약상태: statusNameOf(order.예약상태코드),
    예약상태코드: order.예약상태코드,
    출발일: dayFromNow(order.출발오프셋),
    출발잔여일: daysLeft,
    총판매가: totals.총액,
    입금액: totals.입금액,
    취소료,
    취소규정: policy.설명,
    환불예정액: Math.max(0, totals.입금액 - 취소료),
  };
}

// ── 예약기록(로그) ───────────────────────────────────────────

/** 담당자 이름 → 유저번호. 기록의 작성자를 실제 담당자로 맞추기 위한 것이다. */
function staffNoOf(name) {
  const hit = (DEMO_CODES.담당자 || []).find((c) => c.코드명 === name);
  return hit ? String(hit.코드) : DEMO_USER_NO;
}

/** [시, 분] 에 분을 더한다. 자정을 넘기지 않도록 23:59 에서 멈춘다. */
function addMinutes([hh, mm], minutes) {
  const total = Math.min(23 * 60 + 59, hh * 60 + mm + minutes);
  return [Math.floor(total / 60), total % 60];
}

/** 미래 날짜로 기록이 찍히면 이상하다. 오늘을 넘지 않게 자른다. */
const notFuture = (offset) => Math.min(0, offset);

const won = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

/**
 * 예약기록 (TRP_S_예약기록_판매번호).
 *
 * 화면은 태그로 갈라서 두 군데에 나눠 그린다.
 *   - 태그가 '업무메모' 인 줄  → 예약 상세 우측의 업무메모 카드 (작성자 본인만 수정·삭제 가능)
 *   - 그 밖의 줄              → [예약기록] 버튼을 눌렀을 때 뜨는 표
 * 그래서 두 종류를 다 만들어 둬야 양쪽이 비어 보이지 않는다.
 */
export function buildReservationLogs(order) {
  const totals = orderTotals(order);
  const dates = orderDates(order);
  const 접수시각 = order.예약시각 || [9, 30];
  const 담당번호 = staffNoOf(order.예약담당자);

  const rows = [];
  // 참조번호는 판매마다 겹치면 안 된다. 업무메모 수정·삭제가 이 번호로 대상을 찾기 때문이다.
  let seq = Number(order.판매번호) * 100;

  const add = (태그, 내용, offset, time, mine = false) => {
    seq += 1;
    const [hh, mm] = time;
    rows.push({
      참조번호: seq,
      판매번호: order.판매번호,
      태그,
      내용,
      작성일: dateTimeFromNow(offset, hh, mm),
      유저번호: mine ? DEMO_USER_NO : 담당번호,
      작성자: mine ? DEMO_STAFF.한글이름 : order.예약담당자,
    });
  };

  const 인원설명 = (() => {
    const count = (구분) => (order.여행객 || []).filter((t) => t.연령구분 === 구분).length;
    return ['성인', '아동', '유아']
      .map((구분) => (count(구분) > 0 ? `${구분} ${count(구분)}명` : ''))
      .filter(Boolean)
      .join(' · ');
  })();

  add('예약접수', `${order.주문경로}(으)로 예약이 접수되었습니다. — ${인원설명}`, order.예약일오프셋, 접수시각);
  add('명단', `여행객 명단 ${totals.인원}명이 등록되었습니다.`, order.예약일오프셋, addMinutes(접수시각, 4));

  if (order.대리점번호) {
    add('대리점', `${order.대리점} 접수 건입니다. 확정 회신 대상 대리점으로 지정했습니다.`, order.예약일오프셋, addMinutes(접수시각, 9));
  }

  if (totals.입금액 > 0) {
    const 신청금 = Math.min(totals.입금액, totals.인원 * 200000);
    add('입금', `신청금 ${won(신청금)}이 입금 확인되었습니다.`, notFuture(order.예약일오프셋 + 1), [10, 12]);
    if (totals.입금액 > 신청금) {
      add('입금', `잔금 ${won(totals.입금액 - 신청금)}까지 완납 처리했습니다.`, notFuture(order.예약일오프셋 + 7), [14, 5]);
    }
  }

  if (String(order.예약상태코드) !== '049001') {
    add(
      '상태변경',
      `예약상태를 예약접수 → ${statusNameOf(order.예약상태코드)}(으)로 바꿨습니다.`,
      notFuture(order.예약일오프셋 + 2),
      [11, 30]
    );
  }

  if (isCancelledOrder(order)) {
    add(
      '취소',
      `예약을 취소 처리했습니다. 취소료 ${won(totals.취소료)} 청구 / 환불 ${won(Math.max(0, totals.입금액 - totals.취소료))}.`,
      notFuture(order.예약일오프셋 + 3),
      [16, 20]
    );
  }

  add('행사', `행사일이 ${dates.행사시작일} 로 확정되어 있습니다.`, notFuture(order.예약일오프셋 + 1), [17, 0]);

  // 판매비고는 원래 담당자가 남긴 메모다. 데모 사용자가 수정·삭제를 눌러 볼 수 있게 본인 작성으로 둔다.
  if (String(order.판매비고 || '').trim()) {
    add('업무메모', order.판매비고, notFuture(order.예약일오프셋 + 1), [9, 40], true);
  }
  (order.여행객 || [])
    .filter((t) => String(t.여행객비고 || '').trim())
    .forEach((t, idx) => {
      add('업무메모', `${t.한글이름} — ${t.여행객비고}`, notFuture(order.예약일오프셋 + 2), addMinutes([13, 0], idx * 7), true);
    });

  // 최근 기록이 위로 오게. 실제 기록 화면도 최신순이다.
  return rows.sort((a, b) => String(b.작성일).localeCompare(String(a.작성일)));
}

// ── 통장 입금(수납) 매칭 ─────────────────────────────────────

/**
 * 회사 입금 계좌 (RCT_S_회사기본정보 의 table8).
 * 수납을 손으로 등록할 때 "어느 계좌로 들어왔는지" 고르는 목록이다.
 */
export const DEMO_BANK_ACCOUNTS = [
  { 계좌일련번호: 7001, 은행: '088', 은행명: '신한은행', 계좌번호: '110-000-000000', 예금주: DEMO_COMPANY.업체명, 대표계좌: true },
  { 계좌일련번호: 7002, 은행: '004', 은행명: '국민은행', 계좌번호: '000-00-0000-000', 예금주: DEMO_COMPANY.업체명, 대표계좌: false },
  { 계좌일련번호: 7003, 은행: '020', 은행명: '우리은행', 계좌번호: '0000-000-000000', 예금주: DEMO_COMPANY.업체명, 대표계좌: false },
];

/**
 * 통장에 찍힌 입금 목록 (WSP_S_수납리스트_01).
 *
 * 이 목록은 예약별이 아니라 회사 통장 전체다. 입금은 통장에 먼저 찍히고, 담당자가
 * "이 돈은 이 예약 것" 이라고 손으로 붙여 준다. 그게 예약 상세의 [입금내역 편집] 이 하는 일이다.
 *
 * 화면은 잔액이 0인 줄을 감춘다(이미 다 붙인 돈이니까). 그래서 아직 안 붙은 돈만 여기 남긴다.
 * 돈이 덜 들어온 예약마다 금액이 딱 맞는 줄을 하나씩 깔아 둔 이유는,
 * 데모에서 [입금 적용] 을 눌렀을 때 미수금이 0으로 떨어지는 장면을 보여주기 위해서다.
 */
export function buildBankReceipts() {
  const rows = [];
  let seq = 77000;

  const push = ({ 이름, 금액, 오프셋, 종류 = '계좌이체', 은행명 = '신한은행', 비고 = '' }) => {
    seq += 1;
    rows.push({
      거래번호: `TX${seq}`,
      거래일련번호: seq,
      수납일: dayFromNow(오프셋),
      // 원본 프로시저의 컬럼명이 '송급카드소유자' 다. 오타지만 화면이 이 이름으로 읽으므로 그대로 쓴다.
      송급카드소유자: 이름,
      종류,
      은행명,
      수납액: 금액,
      총입금액: 0,
      잔액: 금액,
      수납통화코드: '058001',
      비고,
    });
  };

  DEMO_ORDERS.forEach((order, idx) => {
    if (isCancelledOrder(order)) return;
    const totals = orderTotals(order);
    if (totals.미수액 <= 0) return;
    push({ 이름: order.예약자.이름, 금액: totals.미수액, 오프셋: -1 - (idx % 4) });
  });

  // 어느 예약과도 안 맞는 입금도 섞어 둔다. 실제 통장은 늘 이렇게 지저분하고,
  // 매칭이라는 일이 왜 필요한지는 이런 줄이 있어야 설명된다.
  push({ 이름: '주식회사더드림', 금액: 1650000, 오프셋: -2, 비고: '대리점 정산 입금' });
  push({ 이름: '윤가람', 금액: 300000, 오프셋: -3, 종류: '카드', 은행명: '국민은행', 비고: '예약번호 미기재' });
  push({ 이름: '최민서', 금액: 400000, 오프셋: 0, 은행명: '우리은행', 비고: '' });

  return rows;
}

// ── 그 밖에 예약 상세가 부르는 것들 ──────────────────────────

/** 대리점(판매 거래처) 목록 (CW_S_판매거래처). 주문정보의 대리점을 바꿀 때 쓰는 드롭다운 재료다. */
export const DEMO_AGENCIES = [
  { 거래처번호: 88001, 업체번호2: '88001', 거래처명: '하나로투어 강남지점', 대표자명: '김하나', 대표전화: '02-000-0001', 사용여부: true },
  { 거래처번호: 88002, 업체번호2: '88002', 거래처명: '더드림투어', 대표자명: '이드림', 대표전화: '02-000-0002', 사용여부: true },
  { 거래처번호: 88003, 업체번호2: '88003', 거래처명: '위투어 부산', 대표자명: '박위투', 대표전화: '051-000-0003', 사용여부: true },
  { 거래처번호: 88004, 업체번호2: '88004', 거래처명: '온라인제휴몰', 대표자명: '정온라', 대표전화: '02-000-0004', 사용여부: true },
];

/**
 * 고객 요청사항 (TRP_S_판매_고객요청사항).
 * 새로 지어내지 않고, 이미 예약에 적혀 있는 비고를 요청사항 형태로 정리해서 보여준다.
 * 같은 사실이 화면마다 다르게 보이면 데모라는 게 바로 티가 나기 때문이다.
 */
export function buildRequestItems(order) {
  const items = [];
  (order.여행객 || [])
    .filter((t) => String(t.여행객비고 || '').trim())
    .forEach((t) => items.push({ 구분: `${t.한글이름} 요청`, 내용: t.여행객비고 }));

  if (order.대리점번호) {
    items.push({
      구분: '대리점 전달사항',
      내용: `${order.대리점}을 통해 접수된 예약입니다. 확정되면 대리점 담당자에게 먼저 회신해 주세요.`,
    });
  }
  return items;
}

/**
 * 판매정보 (TRP_S_예약관리_판매정보).
 * 예약 상세는 이걸로 명단편집 잠금 여부를 읽고, 확인서(인보이스)는 상품명·행사기간을 읽는다.
 */
export function buildSaleInfoRow(order) {
  const dates = orderDates(order);
  const totals = orderTotals(order);
  return {
    ...buildSaleStatusRow(order),
    상품번호: order.상품번호,
    상품코드: order.상품코드,
    상품명: order.상품명,
    판매상품명: order.상품명,
    행사시작일: dates.행사시작일,
    행사종료일: dates.행사종료일,
    출발일: dates.행사시작일,
    총인원: totals.인원,
    행사단체번호: order.행사단체번호,
    행사단체명: order.행사단체명,
    예약담당: order.예약담당자,
    수배담당: order.수배담당자,
    대리점명: order.대리점,
    대리점번호: order.대리점번호,
    판매비고: order.판매비고,
    명단잠금여부: false,
  };
}

// ────────────────────────────────────────────────────────────────
// 상담(견적) 관리
//   ITN_S_상담리스트 / TRP_S_상담정보 / TRP_S_상담번호_상담기록
//
// 상담은 "아직 예약이 아닌 문의" 다. 손님이 물어본 조건을 적어 두고, 원가를 넣어
// 판매가를 정하고, 견적서를 만들어 보내는 자리다. 손님이 결정하면 예약으로 넘어간다.
//
// 상담 한 건은 상품 하나와 짝을 이룬다. 그 연결고리가 "팀컬러" 칸에 들어가는 상품번호다.
// 팀컬러가 비어 있으면 상담상세가 "상품을 새로 만들어 붙이는" 흐름으로 들어가면서
// 도시 선택 모달을 띄운다. 화면에 들어가자마자 모달이 뜨면 데모가 어색해지므로
// 샘플 상담에는 모두 상품번호를 채워 둔다.
// ────────────────────────────────────────────────────────────────

/** 상담상태 코드 ↔ 이름. 상담상세의 상태 드롭다운과 같은 네 가지다. */
export const DEMO_CONSULT_STATUS = [
  { 코드: '420003', 코드명: '상담요청' },
  { 코드: '420001', 코드명: '진행중' },
  { 코드: '420002', 코드명: '예약전환' },
  { 코드: '420999', 코드명: '상담취소' },
];

export function consultStatusNameOf(code) {
  const hit = DEMO_CONSULT_STATUS.find((s) => s.코드 === String(code ?? ''));
  return hit ? hit.코드명 : '';
}

/** 상담정보에 늘 같이 실려 다니는 환율표. 데모는 원화만 쓰므로 원화 한 줄로 충분하다. */
const DEMO_CONSULT_RATES = [
  { 통화코드: '058001', 통화기호: '￦', 적용환율: 1, 실시간환율: 1, 평균환율: 1 },
];

/**
 * 상담 목록.
 *
 * 상태를 골고루 섞어 뒀다. 목록의 상태 배지가 한 종류뿐이면 "상태로 일이 굴러간다" 는
 * 설명이 화면에서 보이지 않기 때문이다.
 *   상담요청 — 아직 아무것도 안 한 새 문의
 *   진행중   — 원가를 넣고 견적을 만들고 있는 건
 *   예약전환 — 손님이 결정해서 예약이 된 건 (상담상세가 잠긴다)
 *   상담취소 — 손님이 접은 건
 *
 * 판매업체 칸은 "이 문의가 어디서 왔는지" 다. 화면은 세 가지로 나눠 보여준다.
 *   우리 업체번호  → '직접등록' (우리가 직접 받은 문의)
 *   대리점 이름    → 그 대리점이 넘긴 문의
 *   아무것도 없음  → '직판'
 */
export const DEMO_CONSULTS = [
  {
    상담번호: 980106,
    상품번호: 990101,
    상담상태코드: '420003',
    견적명: '다낭·호이안 5일 — 대한OO 인센티브 40인 (원가 재계산 중)',
    여행지도시: '다낭',
    요청자명: '한지수',
    요청자휴대폰: '010-0000-0016',
    요청자이메일: 'hanjs@example.com',
    담당자: '김데모',
    담당자번호: '990001',
    판매업체: '',
    판매업체번호: DEMO_COMPANY_NO,
    등록일시: dateTimeFromNow(-1, 17, 40),
    시작일: dayFromNow(75),
    종료일: dayFromNow(79),
    성인수: 38,
    소아수: 2,
    유아수: 0,
    // 판매가를 아직 안 정한 상태. 원가만 넣고 마진을 얹기 전이다.
    성인판매가: 0,
    소아판매가: 0,
    유아판매가: 0,
    세부원가: [
      { 원가항목: '항공료', 성인: 480000, 어린이: 430000, 영유아: 0 },
      { 원가항목: '유류&TAX', 성인: 175000, 어린이: 175000, 영유아: 0 },
      { 원가항목: '지상비', 성인: 205000, 어린이: 170000, 영유아: 0 },
      { 원가항목: '인센티브 행사비', 성인: 60000, 어린이: 60000, 영유아: 0 },
      { 원가항목: '보험', 성인: 5000, 어린이: 5000, 영유아: 0 },
    ],
    견적내용: [
      '40명 단체 인센티브. 4박 5일, 다낭 인/아웃.',
      '· 요청사항: 만찬 1회(현지 한식당), 단체 티셔츠 제작, 전용 버스 45인승',
      '· 항공: 단체석 40석 홀딩 확인 필요 (마감 3일 남음)',
      '· 인솔자 1명 무료 요청 — 원가에 반영 후 1인 판매가 확정 예정',
    ].join('\n'),
    기록: [
      { 등록일시: dateTimeFromNow(-1, 17, 45), 내용: '총무팀 한지수 과장 전화 문의. 예산 1인 110만원 이하 희망.' },
      { 등록일시: dateTimeFromNow(-1, 18, 10), 내용: '항공 단체석 40석 가승인. 3일 내 확정 회신 필요.' },
    ],
  },
  {
    상담번호: 980102,
    상품번호: 990103,
    상담상태코드: '420003',
    견적명: '방콕·파타야 5일 — OO산업 사내 워크샵 22인',
    여행지도시: '방콕',
    요청자명: '박성호',
    요청자휴대폰: '010-0000-0012',
    요청자이메일: 'parksh@example.com',
    담당자: '이샘플',
    담당자번호: '990002',
    판매업체: '하나로투어 강남지점',
    판매업체번호: '88001',
    등록일시: dateTimeFromNow(-2, 11, 5),
    시작일: dayFromNow(61),
    종료일: dayFromNow(65),
    성인수: 20,
    소아수: 2,
    유아수: 0,
    성인판매가: 969000,
    소아판매가: 889000,
    유아판매가: 0,
    세부원가: [
      { 원가항목: '항공료', 성인: 420000, 어린이: 380000, 영유아: 0 },
      { 원가항목: '유류&TAX', 성인: 170000, 어린이: 170000, 영유아: 0 },
      { 원가항목: '지상비', 성인: 190000, 어린이: 160000, 영유아: 0 },
      { 원가항목: '보험', 성인: 5000, 어린이: 5000, 영유아: 0 },
    ],
    견적내용: [
      '대리점(하나로투어 강남지점) 경유 문의. 사내 워크샵 22명.',
      '· 노팁 노옵션 기준으로 요청',
      '· 2인 1실, 팀장 1명 싱글 요청 (싱글차지 별도 안내)',
      '· 파타야 1박 + 방콕 3박',
    ].join('\n'),
    기록: [
      { 등록일시: dateTimeFromNow(-2, 11, 10), 내용: '대리점 담당자 통해 접수. 견적서 오늘 중 회신 약속.' },
    ],
  },
  {
    상담번호: 980101,
    상품번호: 990101,
    상담상태코드: '420001',
    견적명: '다낭·호이안 5일 — 김지현 고객 4인 (리조트 업그레이드)',
    여행지도시: '다낭',
    요청자명: '김지현',
    요청자휴대폰: '010-0000-0011',
    요청자이메일: 'kimjh@example.com',
    담당자: '김데모',
    담당자번호: '990001',
    판매업체: '',
    판매업체번호: DEMO_COMPANY_NO,
    등록일시: dateTimeFromNow(-5, 10, 20),
    시작일: dayFromNow(38),
    종료일: dayFromNow(42),
    성인수: 2,
    소아수: 2,
    유아수: 0,
    성인판매가: 1079000,
    소아판매가: 979000,
    유아판매가: 150000,
    세부원가: [
      { 원가항목: '항공료', 성인: 520000, 어린이: 470000, 영유아: 80000 },
      { 원가항목: '유류&TAX', 성인: 180000, 어린이: 180000, 영유아: 0 },
      { 원가항목: '지상비', 성인: 190000, 어린이: 160000, 영유아: 40000 },
      { 원가항목: '보험', 성인: 5000, 어린이: 5000, 영유아: 2000 },
    ],
    견적내용: [
      '가족 4인(성인 2 / 아동 2) 다낭 4박 5일.',
      '· 숙소: 4성 → 5성 리조트 업그레이드 희망, 차액 안내 요청',
      '· 아동 침대 추가 필요 (7세 / 10세)',
      '· 호이안 야경 일정 포함 희망',
    ].join('\n'),
    기록: [
      { 등록일시: dateTimeFromNow(-5, 10, 25), 내용: '홈페이지 문의 접수. 리조트 업그레이드 차액 확인 요청.' },
      { 등록일시: dateTimeFromNow(-4, 14, 30), 내용: '5성 리조트 1박 차액 6만원(1실) 확인. 4박 기준 24만원 안내함.' },
      { 등록일시: dateTimeFromNow(-2, 9, 15), 내용: '견적서 메일 발송. 이번 주말까지 회신 주신다고 함.' },
    ],
  },
  {
    상담번호: 980104,
    상품번호: 990102,
    상담상태코드: '420001',
    견적명: '다낭 자유여행 4일 — 신혼 2인 (허니문 셋업 요청)',
    여행지도시: '다낭',
    요청자명: '이수민',
    요청자휴대폰: '010-0000-0014',
    요청자이메일: 'leesm@example.com',
    담당자: '김데모',
    담당자번호: '990001',
    판매업체: '',
    판매업체번호: '',
    등록일시: dateTimeFromNow(-9, 16, 0),
    시작일: dayFromNow(27),
    종료일: dayFromNow(30),
    성인수: 2,
    소아수: 0,
    유아수: 0,
    성인판매가: 799000,
    소아판매가: 0,
    유아판매가: 0,
    세부원가: [
      { 원가항목: '항공료', 성인: 430000, 어린이: 0, 영유아: 0 },
      { 원가항목: '유류&TAX', 성인: 150000, 어린이: 0, 영유아: 0 },
      { 원가항목: '지상비', 성인: 120000, 어린이: 0, 영유아: 0 },
      { 원가항목: '보험', 성인: 5000, 어린이: 0, 영유아: 0 },
    ],
    견적내용: [
      '신혼여행 2인. 항공 + 호텔만(에어텔) 기준.',
      '· 허니문 셋업(케이크·데코) 요청 — 호텔 무료 제공 여부 확인 중',
      '· 늦은 체크아웃 희망',
    ].join('\n'),
    기록: [
      { 등록일시: dateTimeFromNow(-9, 16, 5), 내용: '전화 문의. 예산 1인 80만원 내외.' },
      { 등록일시: dateTimeFromNow(-7, 11, 20), 내용: '호텔에 허니문 셋업 무료 요청 메일 발송.' },
    ],
  },
  {
    상담번호: 980103,
    상품번호: 990104,
    상담상태코드: '420002',
    견적명: '오사카·교토 3일 — 정민아 고객 가족 5인',
    여행지도시: '오사카',
    요청자명: '정민아',
    요청자휴대폰: '010-0000-0013',
    요청자이메일: 'jungma@example.com',
    담당자: '박체험',
    담당자번호: '990003',
    판매업체: '더드림투어',
    판매업체번호: '88002',
    등록일시: dateTimeFromNow(-18, 13, 45),
    시작일: dayFromNow(12),
    종료일: dayFromNow(14),
    성인수: 3,
    소아수: 2,
    유아수: 0,
    성인판매가: 710000,
    소아판매가: 650000,
    유아판매가: 0,
    세부원가: [
      { 원가항목: '항공료', 성인: 320000, 어린이: 290000, 영유아: 0 },
      { 원가항목: '유류&TAX', 성인: 120000, 어린이: 120000, 영유아: 0 },
      { 원가항목: '지상비', 성인: 130000, 어린이: 110000, 영유아: 0 },
      { 원가항목: '보험', 성인: 5000, 어린이: 5000, 영유아: 0 },
    ],
    견적내용: [
      '가족 5인 오사카 2박 3일 자유일정. 공항 픽업 포함.',
      '· 확정되어 예약으로 넘어간 건 (예약전환)',
      '· 예약 후 변경 요청은 예약 관리 화면에서 처리한다',
    ].join('\n'),
    기록: [
      { 등록일시: dateTimeFromNow(-18, 13, 50), 내용: '대리점 경유 문의 접수.' },
      { 등록일시: dateTimeFromNow(-15, 10, 5), 내용: '견적 확정. 예약으로 전환하고 신청금 안내함.' },
    ],
  },
  {
    상담번호: 980105,
    상품번호: 990105,
    상담상태코드: '420999',
    견적명: '다낭 반나절 시내투어 — 최윤호 고객 6인 (일정 취소)',
    여행지도시: '다낭',
    요청자명: '최윤호',
    요청자휴대폰: '010-0000-0015',
    요청자이메일: 'choiyh@example.com',
    담당자: '이샘플',
    담당자번호: '990002',
    판매업체: '온라인제휴몰',
    판매업체번호: '88004',
    등록일시: dateTimeFromNow(-24, 9, 10),
    시작일: dayFromNow(9),
    종료일: dayFromNow(9),
    성인수: 6,
    소아수: 0,
    유아수: 0,
    성인판매가: 45000,
    소아판매가: 35000,
    유아판매가: 0,
    세부원가: [
      { 원가항목: '지상비', 성인: 22000, 어린이: 18000, 영유아: 0 },
      { 원가항목: '가이드비', 성인: 6000, 어린이: 6000, 영유아: 0 },
    ],
    견적내용: [
      '현지 출발 반나절 시내투어 6인 문의.',
      '· 고객 현지 일정이 바뀌어 취소된 건',
      '· 취소된 상담도 지우지 않고 남긴다. 왜 안 팔렸는지가 다음 견적의 자료가 된다.',
    ].join('\n'),
    기록: [
      { 등록일시: dateTimeFromNow(-24, 9, 15), 내용: '제휴몰 문의 접수. 출발 3일 전 확정 조건 안내.' },
      { 등록일시: dateTimeFromNow(-20, 15, 40), 내용: '고객 현지 일정 변경으로 취소 회신. 상담취소 처리.' },
    ],
  },
];

export function findDemoConsult(consultNo) {
  const key = String(consultNo ?? '');
  return DEMO_CONSULTS.find((c) => String(c.상담번호) === key) || null;
}

/** 총 인원. 목록의 [인원수] 칸이 이 값을 보여준다. */
export function consultHeadcount(consult) {
  return (
    Number(consult?.성인수 || 0) + Number(consult?.소아수 || 0) + Number(consult?.유아수 || 0)
  );
}

/** 세부원가 항목을 한 사람 기준으로 합친다. (성인 / 어린이 / 영유아) */
export function consultCostSum(consult, column) {
  const rows = Array.isArray(consult?.세부원가) ? consult.세부원가 : [];
  return rows.reduce((sum, row) => sum + Number(row?.[column] || 0), 0);
}

/**
 * 상담 목록 한 줄 (ITN_S_상담리스트).
 * 목록 화면은 상태를 코드가 아니라 이름으로 그리므로 둘 다 담아 준다.
 */
export function buildConsultListRow(consult) {
  const 성인원가 = consultCostSum(consult, '성인');
  const 예상수익 =
    Number(consult.성인수 || 0) * Math.max(0, Number(consult.성인판매가 || 0) - 성인원가);

  return {
    상담번호: consult.상담번호,
    // 팀컬러 = 이 상담에 연결된 상품번호. 목록에서 상품을 찾아가는 유일한 열쇠다.
    팀컬러: String(consult.상품번호 ?? ''),
    견적명: consult.견적명,
    인원수: String(consultHeadcount(consult)),
    상담상태: consultStatusNameOf(consult.상담상태코드),
    상담상태코드: consult.상담상태코드,
    상담구분: '419002',
    판매업체: consult.판매업체 || '',
    판매업체번호: consult.판매업체번호 || '',
    등록일: String(consult.등록일시 || '').slice(0, 10),
    등록일시: consult.등록일시,
    출발일: consult.시작일,
    시작일: consult.시작일,
    종료일: consult.종료일,
    담당자: consult.담당자,
    담당자번호: consult.담당자번호,
    요청자명: consult.요청자명,
    요청자휴대폰: consult.요청자휴대폰,
    요청자이메일: consult.요청자이메일,
    여행지도시: consult.여행지도시,
    예상수익,
  };
}

/**
 * 상담 한 건의 상세 (TRP_S_상담정보 의 상담정보 JSON).
 *
 * 실제 SP 는 이 덩어리를 문자열 한 칸에 담아서 준다. 인터셉터에서도 JSON 문자열로
 * 감싸서 내보내야 화면의 파싱 경로를 그대로 지나간다.
 */
export function buildConsultInfo(consult) {
  const 성인원가 = consultCostSum(consult, '성인');
  const 소아원가 = consultCostSum(consult, '어린이');
  const 유아원가 = consultCostSum(consult, '영유아');
  const 세부원가 = (Array.isArray(consult.세부원가) ? consult.세부원가 : []).map((row) => ({
    원가항목: row.원가항목 || '',
    원가항목코드: '',
    통화기호: 'KRW',
    통화코드: '058001',
    환율적용방식: '전일종가',
    환율적용방식코드: '312001',
    적용환율: 1,
    성인: Number(row.성인 || 0),
    어린이: Number(row.어린이 || 0),
    영유아: Number(row.영유아 || 0),
    원가비고: '',
  }));

  return {
    상담번호: consult.상담번호,
    견적경로: '421001',
    상담구분: '419002',
    상담구분코드: '419002',
    상담상태: consultStatusNameOf(consult.상담상태코드),
    상담상태코드: consult.상담상태코드,
    조인업체명: '',
    // 팀컬러는 배열로 내려온다. 화면이 첫 칸만 읽어서 상품번호로 쓴다.
    팀컬러: consult.상품번호 != null ? [String(consult.상품번호)] : [],
    담당자: consult.담당자,
    담당자번호: Number(consult.담당자번호) || Number(DEMO_USER_NO),
    판매업체: consult.판매업체 || null,
    판매업체번호: consult.판매업체번호 || null,
    수배처: null,
    수배처번호: null,
    요청자명: consult.요청자명,
    요청자휴대폰: consult.요청자휴대폰,
    요청자이메일: consult.요청자이메일,
    시작일: consult.시작일,
    종료일: consult.종료일,
    견적명: consult.견적명,
    견적내용: consult.견적내용,
    여행지도시: consult.여행지도시,
    성인수: Number(consult.성인수 || 0),
    소아수: Number(consult.소아수 || 0),
    유아수: Number(consult.유아수 || 0),
    인솔자수: 0,
    성인판매가: Number(consult.성인판매가 || 0),
    소아판매가: Number(consult.소아판매가 || 0),
    유아판매가: Number(consult.유아판매가 || 0),
    성인원가,
    소아원가,
    유아원가,
    일정표0원표시: false,
    요청일정표: '407002',
    첨부파일리스트: [],
    견적첨부파일리스트: [],
    일정표주소: '',
    적용환율: DEMO_CONSULT_RATES,
    통화코드: '058001',
    통화기호: '￦',
    원가구분: '401002',
    총원가: [
      {
        구분: '총원가',
        통화코드: '058001',
        통화기호: 'KRW',
        적용환율: 1,
        성인: 성인원가,
        어린이: 소아원가,
        영유아: 유아원가,
      },
    ],
    세부원가,
    공용원가: [],
    수익계산: [
      { 항목: '인원수', 번호: 1, 통화코드: '058001', 통화기호: '￦', 성인: Number(consult.성인수 || 0), 어린이: Number(consult.소아수 || 0), 영유아: Number(consult.유아수 || 0) },
      { 항목: '원가', 번호: 2, 통화코드: '058001', 통화기호: '￦', 성인: 성인원가, 어린이: 소아원가, 영유아: 유아원가 },
      { 항목: '판매가', 번호: 3, 통화코드: '058001', 통화기호: '￦', 성인: Number(consult.성인판매가 || 0), 어린이: Number(consult.소아판매가 || 0), 영유아: Number(consult.유아판매가 || 0) },
      {
        항목: '예상수익',
        번호: 4,
        통화코드: '058001',
        통화기호: '￦',
        성인: Number(consult.성인판매가 || 0) - 성인원가,
        어린이: Number(consult.소아판매가 || 0) - 소아원가,
        영유아: Number(consult.유아판매가 || 0) - 유아원가,
      },
    ],
    등록일시: consult.등록일시,
  };
}

/**
 * 상담기록 (TRP_S_상담번호_상담기록).
 * 상담상세 오른쪽 [상담내역] 카드가 이 목록을 최신순으로 그린다.
 */
export function buildConsultNotes(consult) {
  const rows = Array.isArray(consult?.기록) ? consult.기록 : [];
  // 기록번호는 상담끼리 겹치면 안 된다. 삭제가 이 번호만 보고 대상을 찾기 때문이다.
  const base = Number(consult.상담번호) * 100;
  return rows
    .map((note, idx) => ({
      상담기록번호: base + idx + 1,
      상담번호: consult.상담번호,
      내용: note.내용,
      등록자: note.작성자 || consult.담당자,
      등록일시: note.등록일시,
      등록일: String(note.등록일시 || '').slice(0, 10),
      유저번호: consult.담당자번호,
    }))
    .sort((a, b) => String(b.등록일시).localeCompare(String(a.등록일시)));
}
